import React from 'react';
import { useGameStore } from '../store/gameStore';
import { getHandValue, isBlackjack } from '../utils/deck';
import { ShieldIcon, CheckIcon, XIcon } from './Icons';

export const GameControls: React.FC = () => {
  const {
    isPlaying,
    isDealing,
    playerTurnComplete,
    roundComplete,
    hit,
    stand,
    doubleDown,
    split,
    surrender,
    takeInsurance,
    declineInsurance,
    playerHands,
    currentHandIndex,
    dealNewHand,
    speedDrill,
    bankroll,
    settings,
    insuranceOffered,
    lastInsuranceAnalysis
  } = useGameStore();

  // Get the current hand being played
  const currentHand = playerHands[currentHandIndex];
  const { value } = currentHand ? getHandValue(currentHand.cards) : { value: 0 };
  const currentBet = playerHands.reduce((sum, h) => sum + (h.bet || 0), 0);

  const isNaturalBlackjack = currentHand &&
    currentHand.cards.length === 2 &&
    currentHand.cards.every(c => c.faceUp) &&
    isBlackjack(currentHand.cards);

  // Smooth hide for action panels
  const [showActions, setShowActions] = React.useState(false);
  const [actionsExiting, setActionsExiting] = React.useState(false);
  const shouldShowActions = isPlaying && !roundComplete && !playerTurnComplete && currentHand && currentHand.cards.length > 0 && !isNaturalBlackjack && currentHand.result !== 'blackjack' && !insuranceOffered;

  React.useEffect(() => {
    if (shouldShowActions) {
      setActionsExiting(false);
      setShowActions(true);
    } else if (showActions) {
      setActionsExiting(true);
      const t = setTimeout(() => {
        setShowActions(false);
        setActionsExiting(false);
      }, 220);
      return () => clearTimeout(t);
    }
  }, [shouldShowActions, showActions]);

  const [showInsurance, setShowInsurance] = React.useState(false);
  const [insuranceExiting, setInsuranceExiting] = React.useState(false);
  const shouldShowInsurance = insuranceOffered && isPlaying && !roundComplete;

  React.useEffect(() => {
    if (shouldShowInsurance) {
      setInsuranceExiting(false);
      setShowInsurance(true);
    } else if (showInsurance) {
      setInsuranceExiting(true);
      const t = setTimeout(() => {
        setShowInsurance(false);
        setInsuranceExiting(false);
      }, 220);
      return () => clearTimeout(t);
    }
  }, [shouldShowInsurance, showInsurance]);

  // Can't act while cards are being dealt
  const canHit = isPlaying && !roundComplete && !isDealing && !insuranceOffered && value < 21;
  const canStand = isPlaying && !roundComplete && !isDealing && !insuranceOffered;

  // Can only double on first two cards and if we have enough bankroll
  const canDouble = isPlaying && !roundComplete && !isDealing && !insuranceOffered &&
    currentHand && currentHand.cards.length === 2 && currentHand.bet <= bankroll &&
    (playerHands.length === 1 || settings.doubleAfterSplit);

  // Can only split if first two cards have same rank and we have enough bankroll
  const canSplit = isPlaying && !roundComplete && !isDealing && !insuranceOffered &&
    currentHand && currentHand.cards.length === 2 && currentHand.bet <= bankroll &&
    (() => {
      if (!currentHand || currentHand.cards.length !== 2) return false;
      const [c1, c2] = currentHand.cards;
      const r1 = c1.rank === '10' || c1.rank === 'J' || c1.rank === 'Q' || c1.rank === 'K' ? '10' : c1.rank;
      const r2 = c2.rank === '10' || c2.rank === 'J' || c2.rank === 'Q' || c2.rank === 'K' ? '10' : c2.rank;
      // Can't resplit aces unless allowed
      if (r1 === 'A' && playerHands.length > 1 && !settings.resplitAces) return false;
      return r1 === r2;
    })();

  // Can only surrender on first two cards if allowed and not after split
  const allowSurrenderNow =
    (settings.surrenderAllowed && !insuranceOffered) ||
    (settings.earlySurrenderAllowed && insuranceOffered);

  const canSurrender = isPlaying && !roundComplete && !isDealing &&
    currentHand && currentHand.cards.length === 2 && playerHands.length === 1 && allowSurrenderNow;

  // Shared casino-chip style for primary actions to match scenarios
  const getActionStyle = (disabled = false) => ({
    background: disabled
      ? '#9ca3af'
      : 'linear-gradient(135deg, #f9fafb, #e5e7eb, #d1d5db)',
    borderWidth: '3px',
    borderColor: disabled ? 'rgba(107, 114, 128, 0.6)' : 'rgba(156, 163, 175, 0.6)',
    color: disabled ? '#374151' : '#1f2937',
    boxShadow: disabled ? 'none' : '0 2px 6px rgba(0,0,0,0.18)'
  });

  const primaryChipStyle = {
    background: 'linear-gradient(135deg, #ef4444, #dc2626, #b91c1c)',
    borderWidth: '4px',
    borderColor: 'rgba(239, 68, 68, 0.5)'
  };

  // Insurance analysis after decision (avoid showing during prompt)
  const renderInsuranceAnalysis = lastInsuranceAnalysis && !insuranceOffered && (
    <div className={`mt-4 p-3 rounded-lg fade-in-up ${lastInsuranceAnalysis.isCorrect
        ? 'bg-green-900/50 border border-green-600'
        : 'bg-red-900/50 border border-red-600'
      }`}>
      <div className="flex items-center gap-2 mb-1">
        {lastInsuranceAnalysis.isCorrect ? (
          <CheckIcon size={18} className="text-green-400" />
        ) : (
          <XIcon size={18} className="text-red-400" />
        )}
        <span className="font-bold text-white">
          {lastInsuranceAnalysis.isCorrect ? 'Good Call!' : 'Suboptimal'}
        </span>
      </div>
      <p className="text-sm text-gray-300">{lastInsuranceAnalysis.explanation}</p>
      <div className="text-xs text-gray-400 mt-1">
        Expected Value: {lastInsuranceAnalysis.expectedValue > 0 ? '+' : ''}{lastInsuranceAnalysis.expectedValue}%
      </div>
    </div>
  );

  // Don't show controls during speed drill
  if (speedDrill.isActive) {
    return null;
  }

  // Show dealing message while cards are being dealt
  if (isDealing) {
    return null;
  }

  if (showInsurance) {
    const insuranceCost = currentHand ? currentHand.bet / 2 : 0;
    return (
      <div className={`flex flex-col items-center gap-4 ${insuranceExiting ? 'fade-out-down' : 'fade-in-up'}`}>
        <div className="text-center">
          <div className="text-yellow-400 text-lg font-bold mb-2 flex items-center justify-center gap-2">
            <ShieldIcon size={20} />
            Insurance?
          </div>
          <p className="text-gray-300 text-sm mb-3">
            Dealer shows Ace. Take insurance for ${insuranceCost}?
          </p>
          <p className="text-gray-400 text-xs">
            Insurance pays 2:1 if dealer has blackjack
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={takeInsurance}
            disabled={insuranceCost > bankroll}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ✓ Yes (${insuranceCost})
          </button>
          <button
            onClick={declineInsurance}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold shadow-lg transition-all transform hover:scale-105"
          >
            ✗ No Thanks
          </button>
          {settings.earlySurrenderAllowed && (
            <button
              onClick={surrender}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold shadow-lg transition-all transform hover:scale-105"
            >
              Surrender (-50%)
            </button>
          )}
        </div>

      </div>
    );
  }

  // During a hand - show Hit/Stand controls (only while player's turn is active AND cards exist)
  if (showActions) {
    return (
      <div className={`flex flex-col items-center gap-3 ${actionsExiting ? 'fade-out-down' : 'fade-in-up'}`}>
        {/* Show which hand is being played if there are multiple */}
        {playerHands.length > 1 && (
          <div className="text-sm text-yellow-400 mb-1">
            Playing Hand {currentHandIndex + 1} of {playerHands.length}
          </div>
        )}

        <div className="hidden md:flex flex-wrap justify-center gap-2 sm:gap-3 w-full max-w-3xl" role="toolbar" aria-label="Game actions">
          {[{
            label: 'Hit',
            onClick: hit,
            disabled: !canHit,
            ariaKey: 'h',
            focusRing: 'focus:ring-blue-400'
          }, {
            label: 'Stand',
            onClick: stand,
            disabled: !canStand,
            ariaKey: 's',
            focusRing: 'focus:ring-red-400'
          }, {
            label: 'Double',
            onClick: doubleDown,
            disabled: !canDouble,
            ariaKey: 'd',
            focusRing: 'focus:ring-yellow-400'
          }, {
            label: 'Split',
            onClick: split,
            disabled: !canSplit,
            ariaKey: 'p',
            focusRing: 'focus:ring-purple-400'
          }, {
            label: 'Surrender',
            onClick: surrender,
            disabled: !canSurrender,
            ariaKey: 'r',
            focusRing: 'focus:ring-gray-400'
          }].map(({ label, onClick, disabled, ariaKey, focusRing }) => (
            <button
              key={label}
              onClick={onClick}
              disabled={disabled}
              aria-label={`${label} (keyboard shortcut: ${ariaKey.toUpperCase()})`}
              aria-keyshortcuts={ariaKey}
              className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-bold text-sm sm:text-base transition-all relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed ${focusRing} focus:outline-none min-w-[96px] text-center`}
              style={getActionStyle(disabled)}
            >
              <div className="absolute inset-0.5 rounded-full border border-dashed border-gray-400/30 pointer-events-none" />
              <span className="relative">{label}</span>
            </button>
          ))}
        </div>

        {renderInsuranceAnalysis}
      </div>
    );
  }

  // After a hand is complete - show Deal Hand button
  if (roundComplete) {
    return (
      <div className="flex flex-col items-center gap-4">
        <button
          onClick={dealNewHand}
          disabled={currentBet < settings.minBet}
          className="px-7 py-3 rounded-full font-bold text-base sm:text-lg text-white shadow-lg transition-all transform hover:scale-105 relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed fade-in-up"
          style={primaryChipStyle}
        >
          <div className="absolute inset-1 rounded-full border-2 border-dashed border-white/30 pointer-events-none" />
          <span className="relative block leading-none">Deal Next Hand</span>
        </button>
        {renderInsuranceAnalysis}
      </div>
    );
  }

  // Player's turn is complete but dealer is still playing - show waiting message
  if (isPlaying && playerTurnComplete) {
    return null;
  }

  // Not playing yet - show start game prompt
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-center text-gray-300 mb-2">
        <p className="text-lg">Place your bet and deal to start!</p>
        <p className="text-sm text-gray-400">Practice bet sizing based on the true count</p>
      </div>
      <button
        onClick={dealNewHand}
        disabled={currentBet < settings.minBet}
        className="px-6 py-3 rounded-full font-bold text-base text-white shadow-lg transition-all transform hover:scale-105 relative overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
        style={primaryChipStyle}
      >
        <div className="absolute inset-1 rounded-full border-2 border-dashed border-white/30 pointer-events-none" />
        <span className="relative block leading-none">Deal First Hand</span>
      </button>
      {renderInsuranceAnalysis}
    </div>
  );
};
